/**
 * Drop-in replacement for <TileLayer> that reads the user's preferred
 * map source/style and auto-switches to a dark variant in dark mode.
 *
 * Includes a small floating control to let the user switch sources and styles.
 */

import { useState, useEffect, useRef } from 'react'
import { TileLayer, useMap } from 'react-leaflet'
import L from 'leaflet'
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Chip,
  Collapse,
} from '@mui/material'
import LayersIcon from '@mui/icons-material/Layers'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useThemeMode } from '../theme/ThemeProvider'
import {
  MAP_SOURCES,
  resolveMapStyle,
  getSavedMapPref,
  saveMapPref,
  type MapStyle,
} from '../lib/mapStyles'

/**
 * SmartTileLayer - auto-selects tile URL based on user preference + dark mode.
 * Renders the tile layer AND a floating layer-picker control.
 */
export default function MapTileLayer() {
  const { mode } = useThemeMode()
  const isDark = mode === 'dark'
  const [style, setStyle] = useState<MapStyle>(() => resolveMapStyle(isDark))
  const [open, setOpen] = useState(false)
  const pref = getSavedMapPref()
  const [selectedSource, setSelectedSource] = useState(pref.sourceId)
  const panelRef = useRef<HTMLDivElement>(null)

  // Re-resolve when dark mode changes
  useEffect(() => {
    setStyle(resolveMapStyle(isDark))
  }, [isDark])

  // Close panel on click outside
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Stop map drag/scroll events from propagating through the panel
  const map = useMap()
  useEffect(() => {
    if (!panelRef.current) return
    const el = panelRef.current
    L.DomEvent.disableClickPropagation(el)
    L.DomEvent.disableScrollPropagation(el)
  }, [open, map])

  const handleSelect = (sourceId: string, styleId: string) => {
    saveMapPref(sourceId, styleId)
    setStyle(resolveMapStyle(isDark))
    setSelectedSource(sourceId)
    setOpen(false)
  }

  const activeSource = MAP_SOURCES.find((s) => s.id === selectedSource)

  return (
    <>
      <TileLayer
        key={style.url}
        url={style.url}
        attribution={style.attribution}
        maxZoom={style.maxZoom ?? 19}
      />

      {/* Layer picker control */}
      <div
        ref={panelRef}
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 1000,
        }}
      >
        <IconButton
          onClick={() => setOpen((v) => !v)}
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'background.paper' },
            width: 36,
            height: 36,
          }}
          size="small"
        >
          <LayersIcon fontSize="small" />
        </IconButton>

        <Collapse in={open}>
          <Paper
            elevation={4}
            sx={{
              mt: 0.5,
              p: 1.5,
              width: 240,
              maxHeight: 400,
              overflowY: 'auto',
              borderRadius: 2,
            }}
          >
            {MAP_SOURCES.map((source) => (
              <Box key={source.id} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, display: 'block' }}
                >
                  {source.label}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {source.styles.map((s) => {
                    const isActive = style.url === s.url
                    return (
                      <Chip
                        key={s.id}
                        label={s.label}
                        size="small"
                        icon={isActive ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : undefined}
                        onClick={() => handleSelect(source.id, s.id)}
                        color={isActive ? 'primary' : 'default'}
                        variant={isActive ? 'filled' : 'outlined'}
                        sx={{
                          fontSize: 11,
                          fontWeight: isActive ? 700 : 500,
                          cursor: 'pointer',
                        }}
                      />
                    )
                  })}
                </Box>
              </Box>
            ))}
          </Paper>
        </Collapse>
      </div>
    </>
  )
}
